import React, { useRef, useState } from "react";
import { useWorkflow } from "../workflow/WorkflowContext";
import { apiReconcile } from "../lib/api";
import { labelFor as enumLabel } from "../lib/enumLabels";
import { useTranslation } from "react-i18next";

/**
 * v0.4.38 N4 — Reference XLSX reconciliation panel.
 *
 * Mounted on Step 6 (calc detail). Drag-and-drop a reference
 * report XLSX, hit /api/calc/{id}/reconcile, render the row-level
 * delta. Pure presentational state; no calc mutation.
 *
 * The endpoint is fail-loud — UNKNOWN_COLUMNS / INVALID_NET /
 * INVALID_COUNTRY surface as inline red error blocks above the
 * dropzone, never silently swallowed.
 */

// Plain-English labels so the filing agent reading the table
// doesn't need to know internal classifier terminology. Unknown
// codes fall back to the raw value.
// v0.6.10 — dictionary moved to lib/enumLabels so the other
// six components with their own copy can share it.
const labelFor = (code) => enumLabel("bucket", code);

const fmt = (s) => {
  if (s === null || s === undefined || s === "") return "—";
  const n = Number(s);
  if (Number.isNaN(n)) return String(s);
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
};

const errorMessage = (err) => {
  const data = err?.response?.data?.detail;
  if (!data) return err?.message || "Reconcile failed.";
  if (typeof data === "string") return data;
  const code = data.error || "ERROR";
  const msg = data.message || JSON.stringify(data);
  return `${code} — ${msg}`;
};

export const ReconciliationPanel = () => {
  const { t } = useTranslation();
  const { calc } = useWorkflow();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  const [showRows, setShowRows] = useState(false);
  const [fileName, setFileName] = useState(null);

  if (!calc?.id) return null;

  const submit = async (file) => {
    if (!file) return;
    setBusy(true); setErr(null); setResult(null); setShowRows(false);
    setFileName(file.name);
    try {
      const r = await apiReconcile(calc.id, file);
      setResult(r);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) submit(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) submit(f);
  };

  const clear = () => {
    setResult(null); setErr(null); setShowRows(false); setFileName(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const totalDelta = result ? Number(result.summary?.total_delta || 0) : 0;
  const matched = result?.summary?.countries_matched ?? 0;
  const unmatched = result?.summary?.countries_unmatched ?? 0;
  const drifts = result
    ? result.rows.filter((r) => Number(r.delta) !== 0).length
    : 0;
  const isClean = result && totalDelta === 0 && drifts === 0 && unmatched === 0;

  return (
    <section
      data-testid="reconciliation-panel"
      className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/80 p-5 space-y-4"
    >
      <header className="flex items-baseline justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-50">
            {t("ui.reference_reconcile.title", { defaultValue: "Reference reconciliation" })}
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
            {t("ui.reference_reconcile.intro_before", { defaultValue: "Drag a reference report XLSX (columns:" })} <code>Country</code>,
            {" "}<code>NET</code>{t("ui.reference_reconcile.intro_optional", { defaultValue: ", optional" })} <code>Route</code>{t("ui.reference_reconcile.intro_after", { defaultValue: "). The engine diffs your totals against the reference — pure read, nothing written to the calc." })}
          </p>
        </div>
        {result && (
          <button
            type="button"
            data-testid="reconcile-clear-btn"
            data-resolution-class="self-resolve"
            data-resolution-endpoint="navigation"
            onClick={clear}
            className="text-xs text-slate-600 dark:text-slate-300 hover:text-teal-700 dark:hover:text-teal-300 dark:text-teal-300 underline-offset-2 hover:underline"
          >
            {t("ui.reference_reconcile.clear", { defaultValue: "Clear" })}
          </button>
        )}
      </header>

      {err && (
        <div
          data-testid="reconcile-error"
          className="rounded-md border border-rose-200 dark:border-rose-800 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300"
        >
          {err}
        </div>
      )}

      {!result && (
        <div
          data-testid="reconcile-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={[
            "cursor-pointer rounded-lg border-2 border-dashed transition-colors",
            "px-6 py-10 text-center",
            dragOver
              ? "border-teal-500/70 bg-teal-500/5"
              : "border-slate-200 dark:border-slate-700 hover:border-slate-500/80 bg-slate-50 dark:bg-slate-950",
          ].join(" ")}
        >
          <input
            ref={inputRef}
            data-testid="reconcile-file-input"
            type="file"
            accept=".xlsx"
            onChange={onPick}
            className="hidden"
          />
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {busy
              ? t("ui.reference_reconcile.reconciling", { name: fileName || t("ui.reference_reconcile.reference_fallback", { defaultValue: "reference" }), defaultValue: `Reconciling ${fileName || "reference"}…` })
              : t("ui.reference_reconcile.drop_here", { defaultValue: "Drop reference XLSX here, or click to browse" })}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
            {t("ui.reference_reconcile.required", { defaultValue: "Required:" })} <code>Country</code>, <code>NET</code>{t("ui.reference_reconcile.optional_after", { defaultValue: ". Optional:" })} <code>Route</code>.
          </p>
        </div>
      )}

      {result && (
        <>
          <ReconcileBadge
            isClean={isClean}
            matched={matched}
            unmatched={unmatched}
            drifts={drifts}
            totalDelta={totalDelta}
            fileName={fileName}
          />
          <button
            type="button"
            data-testid="reconcile-rows-toggle"
            onClick={() => setShowRows((s) => !s)}
            className="text-xs text-teal-700 dark:text-teal-300 hover:text-teal-700 dark:hover:text-teal-300"
          >
            {showRows ? t("ui.reference_reconcile.hide_rows", { defaultValue: "Hide row detail" }) : t("ui.reference_reconcile.show_rows", { count: result.rows.length, defaultValue: `Show ${result.rows.length} row${result.rows.length === 1 ? "" : "s"}` })}
          </button>
          {showRows && <ReconcileTable rows={result.rows} />}
        </>
      )}
    </section>
  );
};

const ReconcileBadge = ({ isClean, matched, unmatched, drifts, totalDelta, fileName }) => {
  const tone = isClean
    ? "border-emerald-500/40 dark:border-emerald-700 bg-emerald-600/10 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
    : "border-amber-200 dark:border-amber-800 bg-amber-500/10 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300";
  const headline = isClean
    ? `✓ ${matched} ${matched === 1 ? "country matches" : "countries match"}`
    : `⚠ ${drifts || unmatched} ${(drifts || unmatched) === 1 ? "country drifts" : "countries drift"}`;
  return (
    <div
      data-testid="reconcile-status-badge"
      className={`rounded-md border px-3 py-2 text-sm ${tone} flex items-baseline justify-between gap-4`}
    >
      <span className="font-medium">{headline}</span>
      <span className="tabular-nums text-xs">
        {fileName && <span className="opacity-70 mr-2">{fileName}</span>}
        Δ {fmt(String(totalDelta))}
      </span>
    </div>
  );
};

const ReconcileTable = ({ rows }) => { const { t } = useTranslation(); return (
  <div
    data-testid="reconcile-rows-table"
    className="overflow-x-auto rounded-md border border-slate-200 dark:border-slate-700"
  >
    <table className="w-full text-sm">
      <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-300 text-xs uppercase tracking-wide">
        <tr>
          <th className="px-3 py-2 text-left">{t("ui.reference_reconcile.col_country", { defaultValue: "Country" })}</th>
          <th className="px-3 py-2 text-left">{t("ui.reference_reconcile.col_route", { defaultValue: "Route" })}</th>
          <th className="px-3 py-2 text-right">{t("ui.reference_reconcile.col_reference_net", { defaultValue: "Reference NET" })}</th>
          <th className="px-3 py-2 text-right">{t("ui.reference_reconcile.col_engine_net", { defaultValue: "Engine NET" })}</th>
          <th className="px-3 py-2 text-right">Δ</th>
          <th className="px-3 py-2 text-left">{t("ui.reference_reconcile.col_routes_seen", { defaultValue: "Engine routes seen" })}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const drift = Number(r.delta) !== 0;
          return (
            <tr
              key={`${r.country}-${r.route || "any"}-${i}`}
              data-testid={`reconcile-row-${r.country}`}
              className={[
                "border-t border-slate-200 dark:border-slate-700",
                drift ? "bg-amber-500/5" : "",
              ].join(" ")}
            >
              <td className="px-3 py-2 tabular-nums text-slate-900 dark:text-slate-50">{r.country}</td>
              <td className="px-3 py-2 text-slate-500 dark:text-slate-400">
                {r.route ? labelFor(r.route) : "—"}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-800 dark:text-slate-100">
                {fmt(r.reference_net)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-800 dark:text-slate-100">
                {fmt(r.engine_net)}
              </td>
              <td className={[
                "px-3 py-2 text-right tabular-nums",
                drift ? "text-amber-700 dark:text-amber-300" : "text-slate-500 dark:text-slate-400",
              ].join(" ")}>
                {fmt(r.delta)}
              </td>
              <td className="px-3 py-2 text-slate-600 dark:text-slate-300 text-xs">
                {(r.suspect_routing_trace || []).length
                  ? r.suspect_routing_trace.map(labelFor).join(", ")
                  : "—"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
); };
